import { html, LitElement, nothing } from 'lit';
import fsTypography from '@cof/fs-typography-core/lib/typography.lit.min.js';
import '@cof/fs-modal-dialog-wc/fs-modal-dialog.js';
import '@cof/fs-button-wc/fs-button.js';
import '@cof/fs-label-badge-wc/fs-label-badge';
import '@cof/fs-alert-wc/fs-alert.js';
import { TagKey } from '@cof/tag-catalog-fs-esign-mfe';
import { DebugService } from '../../shared/services/debug.service.js';
import { EtitleModalStyles } from './etitle-modal.styles.js';
import { TitleType } from '../etitle.config.js';
import { stratum } from '../../../constants/stratum.js';

export class EtitleModal extends LitElement {
  static properties = {
    etitleType: {
      type: String,
      attribute: 'etitle-type'
    },
    additionalInputs: {
      type: Array,
      attribute: 'additional-inputs'
    },
    loading: {
      type: Boolean
    },
    open: {
      type: Boolean,
      state: true
    },
    showCoBorrowerVerbiage: {
      type: Boolean,
      attribute: 'has-co-borrower'
    },
    eTitleError: {
      type: Boolean
    }
  };

  static styles = [EtitleModalStyles, fsTypography];

  constructor() {
    super();
    this.etitleType = null;
    this.debug = new DebugService(this.className);
    this.loading = false;
    this.additionalInputs = [];
    this.open = false;
    this.showCoBorrowerVerbiage = false;
    this.eTitleError = false;
  }

  onCloseModal() {
    switch (this.etitleType) {
      case TitleType.PoA:
        stratum.publishTag(TagKey.POA_POWER_OF_ATTORNEY_MODAL_CLOSED);
        break;
      case TitleType.TitleAuth:
        stratum.publishTag(
          TagKey.TITLE_AUTHORIZATION_TITLE_AUTHORIZATION_MODAL_CLOSED
        );
        break;
      case TitleType.TitleApp:
        stratum.publishTag(
          TagKey.TITLE_APPLICATION_TITLE_APPLICATION_MODAL_CLOSED
        );
        break;
      case TitleType.SecurityAgreement:
        stratum.publishTag(
          TagKey.SECURITY_AGREEMENT_SECURITY_AGREEMENT_MODAL_CLOSED
        );
        break;
      default:
        break;
    }
    this.eTitleError = false;
  }

  onOpenModal() {
    switch (this.etitleType) {
      case TitleType.PoA:
        stratum.publishTag(TagKey.VIEW_POA);
        stratum.publishTag(TagKey.POA__ESIGN_POWER_OF_ATTORNEY);
        break;
      case TitleType.TitleAuth:
        stratum.publishTag(TagKey.VIEW_TITLE_AUTHORIZATION);
        stratum.publishTag(
          TagKey.TITLE_AUTHORIZATION_ESIGN_TITLE_AUTHORIZATION
        );
        break;
      case TitleType.TitleApp:
        stratum.publishTag(TagKey.VIEW_TITLE_APPLICATION);
        stratum.publishTag(TagKey.TITLE_APPLICATION_ESIGN_TITLE_APPLICATION);
        break;
      case TitleType.SecurityAgreement:
        stratum.publishTag(TagKey.VIEW_SECURITY_AGREEMENT);
        stratum.publishTag(TagKey.SECURITY_AGREEMENT_ESIGN_SECURITY_AGREEMENT);
        break;
      default:
        break;
    }
  }

  submitETitle() {
    if (this.showCoBorrowerVerbiage) {
      stratum.publishTag(TagKey.POATA_WE_AGREE_CLICKED_IN_POATA_MODAL);
    } else {
      switch (this.etitleType) {
        case TitleType.PoA:
          stratum.publishTag(TagKey.POA__I_AGREE_CLICKED_IN_POA_MODAL);
          break;
        case TitleType.TitleAuth:
          stratum.publishTag(
            TagKey.TITLE_AUTHORIZATION_I_AGREE_CLICKED_IN_TITLE_AUTH_MODAL
          );
          break;
        case TitleType.TitleApp:
          stratum.publishTag(
            TagKey.TITLE_APPLICATION_I_AGREE_CLICKED_IN_TITLE_APPLICATION_MODAL
          );
          break;
        case TitleType.SecurityAgreement:
          stratum.publishTag(
            TagKey.SECURITY_AGREEMENT_I_AGREE_CLICKED_IN_SECURITY_AGREEMENT_MODAL
          );
          break;
        default:
          this.debug.error(
            'Unable to find submit tag for eTitle type',
            this.etitleType
          );
          break;
      }
    }
    this.dispatchEvent(
      new CustomEvent('agree-click', { composed: true, bubbles: true })
    );
  }

  inputsAreInvalid() {
    return this.additionalInputs.some(
      input =>
        input.hasError === true || (!input.optional && input.hasError !== false)
    );
  }

  render() {
    return html`
      <fs-modal-dialog
        id="etitleModel"
        accessible-title="eTitle agreement modal"
        dismissible
        size="small"
        aria-labelledby="etitleModel"
        @modal-dialog-close="${this.onCloseModal}">
        <fs-button
          class="fs-button fs-button--primary sign-etitle"
          id="etitle-open"
          @click="${this.onOpenModal}"
          ?disabled="${this.inputsAreInvalid() || this.loading}"
          ?loading="${this.loading}"
          focus-initial
          >E-sign ${this.etitleType}
        </fs-button>
        <fs-heading slot="modal-dialog-header" level="2"
          >E-sign ${this.etitleType}</fs-heading
        >
        <div slot="modal-dialog-body" size="small">
          ${this.eTitleError
            ? html` <fs-alert id="esign-error-alert" type="error" role="alert"
                >We experienced a system error. Please try again
                later.</fs-alert
              >`
            : nothing}
          ${this.showCoBorrowerVerbiage
            ? html` <p>
                By clicking "We agree", my co-borrower and I confirm that we
                have reviewed the ${this.etitleType} form and have verified that
                all information is correct.
              </p>`
            : html`
                <p>
                  By clicking "I agree", I confirm that I have reviewed the
                  ${this.etitleType} form and I have verified that all
                  information is correct.
                </p>
              `}
        </div>
        <fs-modal-dialog-footer
          footer-layout-swap-breakpoint="medium"
          slot="modal-dialog-footer">
          ${this.showCoBorrowerVerbiage
            ? html` <fs-button
                ?loading="${this.loading}"
                ?disabled="${this.loading}"
                variant="primary"
                type="button"
                focus-initial
                @click="${this.submitETitle}"
                id="i-agree-btn"
                >We agree
              </fs-button>`
            : html`
                <fs-button
                  ?loading="${this.loading}"
                  ?disabled="${this.loading}"
                  variant="primary"
                  type="button"
                  focus-initial
                  @click="${this.submitETitle}"
                  id="i-agree-btn"
                  >I agree
                </fs-button>
              `}
        </fs-modal-dialog-footer>
      </fs-modal-dialog>
    `;
  }
}

customElements.define('refi-etitle-modal', EtitleModal);
